import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import SelectDialog, {
  SelectDialogSearchField,
  SelectDialogSubmitButton,
  SelectDialogTable
} from '../../../common/dialogs/SelectDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import ProductAutocomplete from '../components/ProductAutocomplete';
import {
  useFindProductUserNeeds,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  BaseProductAssignDialogProps,
  ProductUserNeed
} from '../Product.interface';
import { getProductEntityName } from '../Product.config';

export interface AssignUserNeedDialogProps
  extends Omit<
    BaseProductAssignDialogProps<never, ProductUserNeed>,
    'onAssigned' | 'productId'
  > {
  productId?: number;
  title?: string;
  contentText?: string;
  isLoading?: boolean;
  defaultLimit?: number;
  onSubmit?: (userNeeds: ProductUserNeed[]) => void;
}

const AssignUserNeedDialog: React.FC<AssignUserNeedDialogProps> = props => {
  const {
    open,
    selectedItems,
    isLoading = false,
    defaultLimit = 5,
    title = `Assign ${getProductEntityName('userNeed')}`,
    contentText = `Search for ${getProductEntityName('userNeed')} to assign.`,
    onCancel,
    onError,
    onSubmit
  } = props;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [productId, setProductId] = useState(props.productId);
  const {
    productUserNeedList,
    isLoadingProductUserNeeds,
    loadingProductUserNeedsError,
    findProductUserNeeds
  } = useFindProductUserNeeds(productId || 0);
  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.UserNeed
  );

  // handle searching
  useEffect(() => {
    const params: ApiParams = { search, page, limit };
    findProductUserNeeds(params);
  }, [search, limit, page, productId, findProductUserNeeds]);

  useEffect(() => {
    if (loadingProductUserNeedsError) {
      onError?.(loadingProductUserNeedsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingProductUserNeedsError]);

  return (
    <SelectDialog open={open} onClose={onCancel} selectedItems={selectedItems}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{contentText}</DialogContentText>
        <SelectDialogSearchField
          label={getProductEntityName('userNeed')}
          onSearch={(value: string) => {
            setPage(0);
            setSearch(value);
          }}
        />
        <Box mt={1} mb={2} className='search-box'>
          <ProductAutocomplete
            productId={productId}
            onChange={product => {
              if (product) {
                setProductId(product.id);
              }
            }}
          />
        </Box>
        <SelectDialogTable
          multiple
          idKey='id'
          columns={[
            {
              key: 'id',
              label: 'ID',
              render: (item: ProductUserNeed) => prefix + item.id
            },
            { key: 'title', label: 'Title', wrap: true },
            {
              key: 'task.status',
              label: 'Status',
              render: (item: ProductUserNeed) => {
                return item.task && <TaskStatusLabel task={item.task} />;
              }
            }
          ]}
          isLoading={isLoadingProductUserNeeds || isLoading}
          dataList={productUserNeedList}
          page={page}
          rowsPerPage={limit}
          onChangePage={page => setPage(page)}
          onChangeRowsPerPage={limit => setLimit(limit)}
        />
        {loadingProductUserNeedsError && (
          <Alert severity='error'>
            Error getting data. Please check your internet connection and try
            again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <SelectDialogSubmitButton onSubmit={onSubmit}>
          Assign
        </SelectDialogSubmitButton>
      </DialogActions>
    </SelectDialog>
  );
};

export default AssignUserNeedDialog;
